import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldSetWrapper } from '~/components/GenericForm/FieldSetWrapper';
import { PercentageField } from '~/components/GenericForm/PercentageField';
import { UnitPositionCustomDataPanel } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/UnitPositionComponent/UnitPositionCustomDataPanel';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/UnitPositionComponent/useStyles';
import {
  UseUnitPositionComponentProps,
  useUnitPositionComponent,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/UnitPositionComponent/useUnitPositionComponent';
import { useUnitPositionCustomData } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/UnitPositionComponent/useUnitPositionCustomData';

type PropsType = UseUnitPositionComponentProps;

export const UnitPositionComponent = (props: PropsType) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const {
    index,
    form: { control },
    collapsedContent: {
      isOpen,
      toggle,
      isToggleDisabled,
      hasError: hasCollapsedContentError,
    },
    fields,
    onChangeUnitPositions,
    unitPositionOptions,
    unitPositionsDisabled,
    selectedUnitPositionValue,
    getUnitPositionInfo,
    recalculateTotal,
  } = useUnitPositionComponent(props);

  const { showCustomData, ...customDataProps } = useUnitPositionCustomData();

  return (
    <FieldSetWrapper>
      <Grid container>
        <Grid item xs={12} sx={sxStyles.fieldAndButton}>
          <Box>
            <Autocomplete
              disabled={unitPositionsDisabled}
              placeholder={' '}
              options={unitPositionOptions}
              getOptionLabel={(c) =>
                `${c.fUnitPositionPosNumber}, ${c.fUnitPositionName}`
              }
              onChange={(e, newValue) => {
                const newUnitPositionIds: number[] = newValue.map(
                  (up) => up.fUnitPositionId,
                );
                onChangeUnitPositions(newUnitPositionIds);
              }}
              value={selectedUnitPositionValue}
              multiple
              size={'small'}
              data-testid="unit-position-autocomplete"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('fEmpUnitPositions')}
                  data-testid="unit-position-input"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              fullWidth={true}
            />
          </Box>
          <Box>
            <Button
              onClick={toggle}
              disabled={isToggleDisabled}
              endIcon={isOpen ? <ArrowDropDown /> : <ArrowDropUp />}
              color={hasCollapsedContentError ? 'error' : 'primary'}
              data-testid="toggle-unit-percentage-button"
            >
              {t('laPercUnitPosition')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Collapse in={isOpen} unmountOnExit easing={'easing'} timeout={500}>
            <Divider sx={{ margin: '16px 0 0 0' }} />
            <Table size={'small'}>
              <TableBody>
                {fields.map((field, upIndex) => {
                  const { name: unitPositionName, customData } =
                    getUnitPositionInfo(field.unitPosition);

                  return (
                    <TableRow key={`unit-position-row-${field.id}`}>
                      <TableCell>{unitPositionName}</TableCell>
                      <TableCell sx={sxStyles.percentageCell}>
                        <Controller
                          control={control}
                          name={`schedule.${index}.empPosition.unitPositionDetails.${upIndex}.unitPositionPercent`}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => {
                            return (
                              <PercentageField
                                value={value ?? ''}
                                onChange={(event) => {
                                  onChange(event);
                                  recalculateTotal();
                                }}
                                error={!!error}
                                helperText={error?.message}
                                size={'small'}
                                fullWidth={true}
                                data-testid="unit-position-percentage-input"
                              />
                            );
                          }}
                        />
                      </TableCell>
                      {showCustomData && (
                        <TableCell>
                          <UnitPositionCustomDataPanel
                            customData={customData}
                            {...customDataProps}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                <Controller
                  control={control}
                  name={`schedule.${index}.empPosition.totalUnitPositionPercentage`}
                  rules={{
                    validate: (value) => {
                      if (fields.length > 1) {
                        return value === 100 || t('errorTotalPercentageNot100');
                      }

                      return (
                        value <= 100 || t('errorTotalPercentageExceeds100')
                      );
                    },
                  }}
                  render={({ field: { value }, fieldState: { error } }) => {
                    return (
                      <>
                        <TableRow>
                          <TableCell sx={sxStyles.totalCell}>
                            {t('total')}
                          </TableCell>
                          <TableCell sx={sxStyles.percentageTotalCell}>
                            {`${value} %`}
                          </TableCell>
                          {showCustomData && <TableCell></TableCell>}
                        </TableRow>
                        {!!error && (
                          <TableRow>
                            <TableCell
                              sx={sxStyles.errorCell}
                              colSpan={showCustomData ? 3 : 2}
                            >
                              {error?.message}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  }}
                />
              </TableBody>
            </Table>
          </Collapse>
        </Grid>
      </Grid>
    </FieldSetWrapper>
  );
};
